/**
 * Execution block: sets a transform property (translation, rotation, scale)
 * on a SceneObject's Babylon transform node.
 *
 * Implements the KHR_interactivity `pointer/set` operation using our internal
 * TransformPath addressing. The `property` config selects which transform
 * component to write:
 *   - "translation" → node.position (Vector3)
 *   - "rotation"    → node.rotationQuaternion (Quaternion, from [x,y,z,w])
 *   - "scale"       → node.scaling (Vector3)
 *
 * Flow outputs:
 *   - `out` — the write happened
 *   - `err` — instead of `out` when the target cannot be resolved or the value
 *     does not match the property. KHR: an invalid pointer or value activates
 *     only `err`.
 *
 * An empty path means the host; a non-empty path that does not resolve fires
 * `err` (`resolveTarget` logs it).
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeAny } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Quaternion } from "@babylonjs/core/Maths/math.vector";
import type { SceneObject } from '../../../core/SceneObject';
import { resolveTarget } from './traceContext';
import { Logger } from '../../../utils/Logger';

export type PointerProperty = 'translation' | 'rotation' | 'scale';

/** Transform components this block can write — anything else is an unknown property. */
const KNOWN_PROPERTIES: ReadonlySet<string> = new Set<PointerProperty>(['translation', 'rotation', 'scale']);

export class PointerSetBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;
    private _property: string;
    public readonly err: FlowGraphSignalConnection;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; property?: string }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._property = config?.property ?? 'translation';
        this.registerDataInput("value", RichTypeAny);
        this.err = this._registerSignalOutput("err");
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (!target) {
            this.err._activateSignal(context);
            return;
        }

        const value: unknown = this.getDataInput("value")!.getValue(context);

        // Resolve the write up front so `err` can fire INSTEAD of `out`.
        const write = resolveWrite(this._property, value);
        if (!write) {
            // Distinguish the two failures, as pointer/interpolate does: an op
            // addressing a property we do not implement is a different problem
            // from a value that does not fit a property we do.
            Logger.error(
                KNOWN_PROPERTIES.has(this._property)
                    ? `[PointerSetBlock] Invalid ${this._property} value on "${target.name}"`
                    : `[PointerSetBlock] Unknown property "${this._property}" on "${target.name}"`,
            );
            this.err._activateSignal(context);
            return;
        }

        write(target);
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "PointerSetBlock";
    }
}

/**
 * Bind a property + value to the write it performs, or null when the value does
 * not match the property (→ `err`). Resolving before writing is what lets
 * `err` fire instead of `out` rather than after a partial write.
 */
function resolveWrite(property: string, value: unknown): ((target: SceneObject) => void) | null {
    switch (property) {
        case 'translation': {
            const v = toVec3(value);
            return v ? (t: SceneObject) => t.setPosition(v) : null;
        }
        case 'rotation': {
            const q = toQuat(value);
            return q ? (t: SceneObject) => t.setRotation(q) : null;
        }
        case 'scale': {
            const v = toVec3(value);
            return v ? (t: SceneObject) => t.setScale(v) : null;
        }
        default:
            return null;
    }
}

/** Convert an input value to a Vector3 (accepts [x,y,z] arrays or {x,y,z} objects). */
export function toVec3(value: unknown): Vector3 | null {
    if (value instanceof Vector3) return value;
    if (Array.isArray(value) && value.length >= 3) {
        return new Vector3(value[0] as number, value[1] as number, value[2] as number);
    }
    if (value != null && typeof value === 'object' && 'x' in value && 'y' in value && 'z' in value) {
        const v = value as { x: number; y: number; z: number };
        return new Vector3(v.x, v.y, v.z);
    }
    return null;
}

/** Convert an input value to a Quaternion (accepts [x,y,z,w] arrays or {x,y,z,w} objects). */
export function toQuat(value: unknown): Quaternion | null {
    if (value instanceof Quaternion) return value;
    if (Array.isArray(value) && value.length >= 4) {
        return new Quaternion(value[0] as number, value[1] as number, value[2] as number, value[3] as number);
    }
    if (value != null && typeof value === 'object' && 'x' in value && 'y' in value && 'z' in value && 'w' in value) {
        const v = value as { x: number; y: number; z: number; w: number };
        return new Quaternion(v.x, v.y, v.z, v.w);
    }
    return null;
}
